import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Bell, Menu, X, Globe, LogOut, LayoutDashboard, Shield, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { isAuthenticated, isAdmin, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-[#0A0A0A]">VisaAlerts</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-[#0A0A0A]' : 'text-gray-600 hover:text-[#0A0A0A]'}`}
              data-testid="nav-home"
            >
              {t('nav_home')}
            </Link>
            <a 
              href="/#pricing" 
              className="text-sm font-medium text-gray-600 hover:text-[#0A0A0A] transition-colors"
              data-testid="nav-pricing"
            >
              {t('nav_pricing')}
            </a>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors ${isActive('/contact') ? 'text-[#0A0A0A]' : 'text-gray-600 hover:text-[#0A0A0A]'}`}
              data-testid="nav-contact"
            >
              {t('nav_contact')}
            </Link>
            
            {isAuthenticated && (
              <Link 
                to="/dashboard" 
                className={`text-sm font-medium transition-colors ${isActive('/dashboard') ? 'text-[#0A0A0A]' : 'text-gray-600 hover:text-[#0A0A0A]'}`}
                data-testid="nav-dashboard"
              >
                {t('nav_dashboard')}
              </Link>
            )}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="gap-1 text-gray-600"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language.toUpperCase()}
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    <div className="w-6 h-6 rounded-full bg-[#0A0A0A] flex items-center justify-center text-white text-xs font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    {user?.name?.split(' ')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" />
                    {t('nav_profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav_dashboard')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login">
                  {t('nav_login')}
                </Button>
                <Button 
                  size="sm" 
                  className="bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white"
                  onClick={() => navigate('/register')}
                  data-testid="nav-register"
                >
                  {t('nav_register')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <Link to="/" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                {t('nav_home')}
              </Link>
              <a href="/#pricing" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                {t('nav_pricing')}
              </a>
              <Link to="/contact" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                {t('nav_contact')}
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav_profile')}
                  </Link>
                  <Link to="/dashboard" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav_dashboard')}
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                      Admin
                    </Link>
                  )}
                  <button onClick={handleLogout} className="px-2 py-2 text-sm font-medium text-red-600 text-left">
                    {t('nav_logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav_login')}
                  </Link>
                  <Link to="/register" className="px-2 py-2 text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                    {t('nav_register')}
                  </Link>
                </>
              )}
              
              <button onClick={toggleLanguage} className="px-2 py-2 text-sm font-medium flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'Français' : 'English'}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
